import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square } from "lucide-react";

export default function VoiceTester() {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState("");
  const [status, setStatus] = useState("idle"); // idle | recording | thinking | speaking
  const [transcript, setTranscript] = useState([]);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const localStreamRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const convIdRef = useRef(null);

  useEffect(() => { api.get("/agents").then((r) => { setAgents(r.data); if (r.data[0]) setSelected(r.data[0].id); }); }, []);

  const start = async () => {
    if (!selected || status !== "idle") return;
    setStatus("recording");
    try {
      const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = ms;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(ms, { mimeType: mime });
      mediaRecorderRef.current = rec;
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        const blob = new Blob(audioChunksRef.current, { type: mime });
        audioChunksRef.current = [];
        await sendTurn(blob, mime);
      };
      rec.start();
    } catch (err) {
      toast.error(err.message || "Mic access denied");
      setStatus("idle");
    }
  };

  const sendTurn = async (blob, mime) => {
    setStatus("thinking");
    const fd = new FormData();
    const ext = mime.includes("webm") ? "webm" : "mp4";
    fd.append("audio", blob, `voice.${ext}`);
    fd.append("agent_id", selected);
    if (convIdRef.current) fd.append("conversation_id", convIdRef.current);
    let data;
    try {
      const res = await api.post("/voice/cascade-turn", fd, { headers: { "Content-Type": "multipart/form-data" } });
      data = res.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Voice turn failed");
      setStatus("idle");
      return;
    }
    convIdRef.current = data.conversation_id;
    setTranscript((t) => [
      ...t,
      { role: "user", content: data.user_text },
      { role: "assistant", content: data.assistant_text },
    ]);
    try {
      const audio = new Audio(`data:${data.audio_mime || "audio/mpeg"};base64,${data.audio_base64}`);
      audioPlayerRef.current = audio;
      setStatus("speaking");
      audio.onended = () => setStatus("idle");
      audio.onerror = () => setStatus("idle");
      await audio.play();
    } catch {
      setStatus("idle");
    }
  };

  const stop = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* noop */ }
    } else {
      try { audioPlayerRef.current?.pause(); } catch { /* noop */ }
      try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      setStatus("idle");
    }
  };

  const statusColor = status === "recording" ? "text-[#10B981]" : (status === "thinking" || status === "speaking") ? "text-[#F59E0B]" : "text-[#4B5563]";

  return (
    <div data-testid="voice-tester-page" className="space-y-6">
      <div>
        <h1 className="font-display font-extrabold text-3xl">Web Voice Tester</h1>
        <p className="text-sm text-[#4B5563]">Cascade pipeline · Whisper STT → GPT-4o-mini → OpenAI TTS · ~16× cheaper than Realtime.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="hs-card p-6 lg:col-span-1">
          <label className="text-xs font-mono uppercase tracking-wide text-[#4B5563]">Agent</label>
          <select data-testid="voice-agent-select" value={selected} onChange={(e) => setSelected(e.target.value)} disabled={status !== "idle"} className="w-full border border-[#E2E8F0] mt-2 p-2 rounded-sm bg-white text-sm">
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>

          <div className="mt-8 flex justify-center">
            {status === "idle" && (
              <button data-testid="voice-start-button" onClick={start} disabled={!selected} className="w-28 h-28 rounded-full bg-[#0044FF] hover:bg-[#0033CC] text-white flex items-center justify-center transition-colors">
                <Mic className="w-10 h-10" />
              </button>
            )}
            {status === "recording" && (
              <button data-testid="voice-stop-button" onClick={stop} className="w-28 h-28 rounded-full bg-[#EF4444] hover:bg-red-700 text-white flex items-center justify-center animate-pulse">
                <Square className="w-9 h-9" />
              </button>
            )}
            {(status === "thinking" || status === "speaking") && (
              <button disabled data-testid="voice-busy-button" className="w-28 h-28 rounded-full bg-[#E2E8F0] text-[#0F1115] flex items-center justify-center">
                <div className="w-9 h-9 border-4 border-[#0044FF]/30 border-t-[#0044FF] rounded-full animate-spin" />
              </button>
            )}
          </div>
          <div className="text-center mt-4 text-xs font-mono">
            status: <span className={statusColor}>{status}</span>
          </div>
        </div>

        <div className="hs-card p-6 lg:col-span-2 min-h-[420px]">
          <div className="font-display font-bold">Live transcript</div>
          <div className="text-xs font-mono text-[#4B5563] mb-4">cascade · whisper + gpt-4o-mini + tts-1</div>
          {transcript.length === 0 && <div className="text-xs text-[#4B5563]">Tap the mic, speak for a few seconds, then tap stop. The agent will reply with voice + transcript.</div>}
          <div className="space-y-2 font-mono text-sm">
            {transcript.map((m, i) => (
              <div key={i}><span className={m.role === "assistant" ? "text-[#0044FF]" : "text-[#030712] font-bold"}>{m.role.toUpperCase()}</span> {m.content}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
